import React from 'react';
import { alpha, Box, Card, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import moment from 'moment';
import ExportButtons from '../../services/sextforce/metrics/account/performanceStats/ExportButtons';
import { formatCompactNumber } from '../dashboardAnalytics.utils';

type ProfileVisitorsTrendCardProps = {
    profileVisitorsLoadedCount: number;
    maxCount: number;
    visitorsTrendChartExportData: Array<Record<string, string | number>>;
    exportFilename: string;
    profileVisitorsChartData: Array<{ time: number; value: number }>;
    profileVisitorsComparisonChartData: Array<{ time: number; comparisonValue: number }>;
    profileVisitorsYAxisDomain: [number, number];
    rangePreset: '7d' | '30d' | '90d' | '1y' | 'all' | 'custom';
    profileVisitorsCurrentLegendDateRange: string | null;
    profileVisitorsComparisonLegendDateRange: string | null;
    showComparison: boolean;
    profileVisitorsPeakLowStat: { maxPt: { time: number; value: number }; minPt: { time: number; value: number } } | null;
    profileVisitorsPeriodChangePercent: number | null;
};

const ProfileVisitorsTrendCard = React.memo((props: ProfileVisitorsTrendCardProps) => {
    const theme = useTheme();
    const {
        profileVisitorsLoadedCount,
        maxCount,
        visitorsTrendChartExportData,
        exportFilename,
        profileVisitorsChartData,
        profileVisitorsComparisonChartData,
        profileVisitorsYAxisDomain,
        rangePreset,
        profileVisitorsCurrentLegendDateRange,
        profileVisitorsComparisonLegendDateRange,
        showComparison,
        profileVisitorsPeakLowStat,
        profileVisitorsPeriodChangePercent,
    } = props;

    return (
        <Card sx={{ p: 1.5 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h6" color="primary.main">
                    Profile Visitors Trend
                </Typography>
                <ExportButtons data={visitorsTrendChartExportData} componentType="report" filename={exportFilename} />
            </Stack>
            {profileVisitorsLoadedCount < maxCount ? (
                <Skeleton height={220} />
            ) : (
                <>
                    <Box sx={{ width: '100%', height: 220, mb: 1.5 }}>
                        {profileVisitorsChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={profileVisitorsChartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="profileVisitorsTrendFill" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.45} />
                                            <stop offset="90%" stopColor={theme.palette.primary.main} stopOpacity={0.04} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid stroke={theme.palette.divider} strokeOpacity={0.35} vertical={false} />
                                    <XAxis
                                        dataKey="time"
                                        type="number"
                                        domain={['dataMin', 'dataMax']}
                                        tickCount={rangePreset === '1y' || rangePreset === 'all' ? 8 : 6}
                                        minTickGap={36}
                                        tickMargin={8}
                                        tickFormatter={(value) =>
                                            rangePreset === '1y' || rangePreset === 'all'
                                                ? moment(value).format('MMM, YYYY')
                                                : moment(value).format('MMM D')
                                        }
                                        tick={{ fontSize: 11 }}
                                        stroke={theme.palette.text.secondary}
                                    />
                                    <YAxis
                                        width={50}
                                        tickCount={6}
                                        domain={profileVisitorsYAxisDomain}
                                        tickFormatter={(value: number) => formatCompactNumber(value)}
                                        tick={{ fontSize: 11 }}
                                        stroke={theme.palette.text.secondary}
                                    />
                                    <RechartsTooltip
                                        formatter={(value: number) => [Number(value ?? 0).toLocaleString(), 'Visitors']}
                                        labelFormatter={(label) => moment(label).format('ddd, MMM D')}
                                    />
                                    <Area
                                        type="linear"
                                        data={profileVisitorsComparisonChartData}
                                        dataKey="comparisonValue"
                                        stroke={theme.palette.grey[500]}
                                        fillOpacity={0}
                                        strokeDasharray="4 4"
                                        strokeWidth={2}
                                        dot={false}
                                        isAnimationActive={false}
                                    />
                                    <Area
                                        type="linear"
                                        dataKey="value"
                                        stroke={theme.palette.primary.dark}
                                        fill="url(#profileVisitorsTrendFill)"
                                        strokeWidth={2.4}
                                        dot={false}
                                        activeDot={{ r: 4 }}
                                        isAnimationActive={false}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography color="text.secondary">No visitors chart data available.</Typography>
                            </Box>
                        )}
                    </Box>
                    {profileVisitorsCurrentLegendDateRange ? (
                        <Stack direction="row" flexWrap="wrap" justifyContent="center" alignItems="center" spacing={3} sx={{ columnGap: 3, rowGap: 1.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Box sx={{ width: 32, height: 3, flexShrink: 0, borderRadius: 1, bgcolor: 'primary.dark' }} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                                    <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 0.5 }}>Current Period:</Box>
                                    {profileVisitorsCurrentLegendDateRange}
                                </Typography>
                            </Stack>
                            {showComparison && profileVisitorsComparisonLegendDateRange ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Box sx={{ width: 32, flexShrink: 0, borderTop: `2px dashed ${theme.palette.grey[500]}`, mt: '2px' }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
                                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 0.5 }}>Comparison Period:</Box>
                                        {profileVisitorsComparisonLegendDateRange}
                                    </Typography>
                                </Stack>
                            ) : null}
                        </Stack>
                    ) : null}
                    {profileVisitorsChartData.length > 0 && profileVisitorsPeakLowStat ? (
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch', bgcolor: (t) => alpha(t.palette.primary.main, 0.08), borderRadius: (t) => t.shape.borderRadius, overflow: 'hidden' }}>
                            <Box sx={{ flex: 1, py: 2, px: 2, textAlign: 'center', position: 'relative', '&::after': { content: '""', display: 'block', position: 'absolute', right: { xs: 14, sm: 0 }, left: { xs: 14, sm: 'auto' }, top: { xs: 'auto', sm: 14 }, bottom: { xs: 0, sm: 14 }, width: { xs: 'auto', sm: '1px' }, height: { xs: '1px', sm: 'auto' }, bgcolor: 'divider' } }}>
                                <Typography variant="body2" color="text.secondary" display="block">vs Previous Period</Typography>
                                {!showComparison || profileVisitorsPeriodChangePercent === null ? (
                                    <Typography variant="h6" sx={{ mt: 0.75, fontWeight: 700, color: 'text.secondary' }}>N/A</Typography>
                                ) : (
                                    <Typography variant="h6" sx={{ mt: 0.75, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: profileVisitorsPeriodChangePercent >= 0 ? 'success.main' : 'error.main' }}>
                                        {profileVisitorsPeriodChangePercent >= 0 ? '↑' : '↓'} {Math.abs(profileVisitorsPeriodChangePercent).toFixed(1)}%
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ flex: 1, py: 2, px: 2, textAlign: 'center', position: 'relative', '&::after': { content: '""', display: 'block', position: 'absolute', right: { xs: 14, sm: 0 }, left: { xs: 14, sm: 'auto' }, top: { xs: 'auto', sm: 14 }, bottom: { xs: 0, sm: 14 }, width: { xs: 'auto', sm: '1px' }, height: { xs: '1px', sm: 'auto' }, bgcolor: 'divider' } }}>
                                <Typography variant="body2" color="text.secondary" display="block">Peak Visits</Typography>
                                <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.35 }}>
                                    {moment.utc(profileVisitorsPeakLowStat.maxPt.time).format('MMM D, YYYY')}
                                </Typography>
                                <Typography variant="h6" color="primary.main" sx={{ mt: 0.75, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                    {profileVisitorsPeakLowStat.maxPt.value.toLocaleString()}
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, py: 2, px: 2, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" display="block">Lowest Visits</Typography>
                                <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.35 }}>
                                    {moment.utc(profileVisitorsPeakLowStat.minPt.time).format('MMM D, YYYY')}
                                </Typography>
                                <Typography variant="h6" color="primary.main" sx={{ mt: 0.75, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                    {profileVisitorsPeakLowStat.minPt.value.toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    ) : null}
                </>
            )}
        </Card>
    );
});

ProfileVisitorsTrendCard.displayName = 'ProfileVisitorsTrendCard';
export default ProfileVisitorsTrendCard;
